import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import type { Corners } from './scanProcessing';

const HANDLE_KEYS = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'] as const;
type HandleKey = (typeof HANDLE_KEYS)[number];

interface Props {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  corners: Corners;
  onChange: (corners: Corners) => void;
}

/** Vollständig kontrollierte Komponente - der Aufrufer hält den Eckpunkte-State, hier passiert nur Drag-Interaktion + Darstellung. */
export function CornerAdjuster({ imageUrl, imageWidth, imageHeight, corners, onChange }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingKey = useRef<HandleKey | null>(null);

  function pointerToImageCoords(clientX: number, clientY: number) {
    const rect = containerRef.current!.getBoundingClientRect();
    const xFrac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const yFrac = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    return { x: xFrac * imageWidth, y: yFrac * imageHeight };
  }

  function handlePointerDown(key: HandleKey) {
    return (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      draggingKey.current = key;
      e.currentTarget.setPointerCapture(e.pointerId);
    };
  }

  function handlePointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const key = draggingKey.current;
    if (!key) return;
    onChange({ ...corners, [key]: pointerToImageCoords(e.clientX, e.clientY) });
  }

  function handlePointerUp() {
    draggingKey.current = null;
  }

  const percent = (p: { x: number; y: number }) => ({ left: `${(p.x / imageWidth) * 100}%`, top: `${(p.y / imageHeight) * 100}%` });
  const polygonPoints = HANDLE_KEYS.map((k) => `${(corners[k].x / imageWidth) * 100},${(corners[k].y / imageHeight) * 100}`).join(' ');

  return (
    <div
      ref={containerRef}
      className="relative mx-auto w-fit touch-none select-none overflow-hidden rounded-md"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {/* Bild wird auf die verfügbare Bildschirmhöhe begrenzt (statt volle Breite mit "height:auto") -
          bei hochformatigen Handyfotos würden sonst die unteren Eckpunkte außerhalb des sichtbaren
          Bereichs landen und wären nur durch Scrollen erreichbar. */}
      <img src={imageUrl} alt="Aufgenommenes Dokument" className="block max-h-[58vh] w-auto max-w-full" draggable={false} />
      <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <polygon points={polygonPoints} fill="rgba(250,204,21,0.18)" stroke="rgb(250 204 21)" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
      </svg>
      {HANDLE_KEYS.map((key) => (
        <div
          key={key}
          onPointerDown={handlePointerDown(key)}
          className="absolute h-9 w-9 -translate-x-1/2 -translate-y-1/2 touch-none rounded-full border-2 border-accent-400 bg-accent-400/40 shadow-lg active:bg-accent-400/70"
          style={percent(corners[key])}
        />
      ))}
    </div>
  );
}
